import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import FileDownload from 'js-file-download';
import { request } from '~/components/Instance';
import { Icon, Dropdown, Menu, Button, Modal, Statistic, Row, Col, Card, message, Popover, Badge } from 'antd';
import { toBRL } from '~/components/UI/InputCurrency';
import moment from 'moment';
import uid from 'uid';

import * as drawerIRPFActions from '~/containers/DrawerIRPF/actions';

import { YearPicker } from '~/components/UI/YearPicker';

import { PERMISSIONS } from '~/constants/permissions.constants';
import { ModuleHeader } from '~/components/ModuleHeader';
import Search from '~/_shared/components/Search';
import FilterPillsBar from '~/_shared/components/FilterPillsBar';
import RestoredFiltersNotice from '~/_shared/components/RestoredFiltersNotice';
import TableList from './TableList';
import { countActiveIrpfFilters, irpfYearToMoment } from '../helpers';

const List = (props) => {
  // Applied filters live in the screen (usePersistedFilters), which reloads the
  // list whenever the year changes; the other filters are client-side.
  var persisted = props.persisted;
  var applied = props.applied;
  var defaults = persisted.defaults;
  var setApplied = persisted.setFilters;
  var search = applied.search;
  var showActive = applied.showActive;
  var statusFilter = applied.statusFilter;
  var setSearch = function(value) { setApplied({ search: value }); };
  var setShowActive = function(value) { setApplied({ showActive: value }); };
  var setStatusFilter = function(value) { setApplied({ statusFilter: value }); };

  const [payment, setPayment] = useState({
    paid: {
      honorary: 0,
      additionals: 0
    },
    pendent: {
      honorary: 0,
      additionals: 0
    }
  })
  const [loadingRowId, setLoadingRowId] = useState(null);

  const {
    showDrawer,
    customers,
    archiveCustomer,
    activateCustomer,
    startService,
    user,
    isLoading
  } = props;

  // Always the applied (persisted) year, so the UI never shows a year other
  // than the one being loaded; the screen mirrors it into redux current_year.
  var currentYear = React.useMemo(function() {
    return irpfYearToMoment(applied.year);
  }, [applied.year]);

  useEffect(() => {
    const honorariesOfCurrentYear = customers
      .filter((customer) => !customer.archived_at && !customer.archived_by)
      .flatMap((customer) => customer.honoraries || [])
      .filter((honorary) => honorary.year === currentYear.year());

    const paid = honorariesOfCurrentYear.reduce((total, honorary) => {
      if (!honorary.paid_at) return total;
      const additionalsSum = (honorary.additionals || []).reduce((acc, cur) => acc + cur.amount, 0);
      return {
        honorary: total.honorary + honorary.amount,
        additionals: total.additionals + additionalsSum
      };
    }, { honorary: 0, additionals: 0 });

    const pendent = honorariesOfCurrentYear.reduce((total, honorary) => {
      if (!!honorary.paid_at) return total;
      const additionalsSum = (honorary.additionals || []).reduce((acc, cur) => acc + cur.amount, 0);
      return {
        honorary: total.honorary + honorary.amount,
        additionals: total.additionals + additionalsSum
      };
    }, { honorary: 0, additionals: 0 });

    setPayment({
      paid,
      pendent
    })
  }, [customers, currentYear]);

  const onAction = (e, customer) => {
    switch(e.key){
      case 'renew':
        setLoadingRowId(customer.id);
        startService(customer.id, { uid: uid(10), year: currentYear.year(), amount: 0 }, currentYear.year())
          .then(function(res) {
            var data = res && res.payload && res.payload.data && res.payload.data.data;
            if (data) showDrawer(data);
          })
          .finally(function() { setLoadingRowId(null); });
      break;
      case 'edit':
      showDrawer(customer);
      break;
      case 'archive':
        Modal.confirm({
          title: 'Você realmente deseja arquivar este cliente?',
          content: 'Esta ação poderá ser desfeita.',
          okText: 'Sim',
          okType: 'danger',
          cancelText: 'Cancelar',
          onOk: () => archiveCustomer(customer.id, currentYear.year())
        });
      break;
      case 'activate':
          activateCustomer(customer.id, currentYear.year())
      break;
      default:
        return null;
    }
  }

  const handleExportToExcel = async () => {
    try {
      const response = await request('POST', `/pf/export/excel?year=${currentYear.year()}`, {}, true, {
        responseType: 'blob'
    })
      FileDownload(response.data, `IRPF_${currentYear.year()}.xlsx`);
    } catch (error) {
      message.error('Não foi possível realizar a exportação!')
    }
  }

  const filtered = (customers !== null)? customers.filter((str) => {

    if(str.name.toLowerCase().indexOf(search.toLowerCase()) >= 0){
      return true;
    }

    if(str.origem && str.origem.toLowerCase().indexOf(search.toLowerCase()) >= 0){
      return true;
    }

    return false;
  }).map((customer) => {
    const honoraryOfCurrentYear = (customer.honoraries || []).find((honorary) => Number(honorary.year) === currentYear.year());
    return {
      ...customer,
      honorary_of_current_year: honoraryOfCurrentYear
    }
  }).sort((a, b) => a.name.localeCompare(b.name)) : [];

  const activeCustomers = filtered.filter((customer) => !customer.archived_at && !customer.archived_by);

  const archivedCustomers = filtered.filter((customer) => !!customer.archived_at && !!customer.archived_by);

  const canSee = user.permissions.includes(PERMISSIONS.irpf.summary.view);

  var baseDisplayed = showActive ? activeCustomers : archivedCustomers;

  function getStatusKey(customer) {
    var h = customer.honorary_of_current_year;
    if (!h) return 'anoAnterior';
    if (h.process_step === 1) return 'concluido';
    if (h.process_step === 2) return 'emProcesso';
    return 'pendente';
  }

  var statusCounts = React.useMemo(function() {
    var counts = { anoAnterior: 0, pendente: 0, emProcesso: 0, concluido: 0 };
    for (var i = 0; i < baseDisplayed.length; i++) {
      counts[getStatusKey(baseDisplayed[i])]++;
    }
    return counts;
  }, [baseDisplayed]);

  var displayedCustomers = React.useMemo(function() {
    if (!statusFilter) return baseDisplayed;
    return baseDisplayed.filter(function(c) { return getStatusKey(c) === statusFilter; });
  }, [baseDisplayed, statusFilter]);

  //fix for widescreens
  const additional = window.innerWidth > 1440 ? 200:0;

  var STATUS_FILTER_OPTIONS = [
    { key: 'concluido', label: 'Concluído', color: '#a0d911', count: statusCounts.concluido },
    { key: 'emProcesso', label: 'Em processo', color: '#fadb14', count: statusCounts.emProcesso },
    { key: 'pendente', label: 'Pendente', color: '#f5222d', count: statusCounts.pendente },
    { key: 'anoAnterior', label: 'Ano anterior', color: '#bfbfbf', count: statusCounts.anoAnterior },
  ];

  const isDefaultYear = applied.year === defaults.year;
  const isDefaultStatus = showActive === defaults.showActive;
  const filterCount = countActiveIrpfFilters(applied, defaults);

  const filterPills = [];
  filterPills.push({
    key: 'year',
    label: 'Ano: ' + currentYear.year(),
    color: '#1890ff',
    icon: 'calendar',
    onRemove: !isDefaultYear ? function() { setApplied({ year: defaults.year }); } : undefined,
  });
  filterPills.push({
    key: 'visibility',
    label: showActive ? 'Ativos' : 'Arquivados',
    color: showActive ? '#a0d911' : '#f5222d',
    icon: showActive ? 'check-circle' : 'inbox',
    onRemove: !isDefaultStatus ? function() { setShowActive(defaults.showActive); } : undefined,
  });
  if (statusFilter) {
    var sfOpt = STATUS_FILTER_OPTIONS.find(function(o) { return o.key === statusFilter; });
    if (sfOpt) {
      filterPills.push({
        key: 'processStatus',
        label: sfOpt.label,
        color: sfOpt.color,
        onRemove: function() { setStatusFilter(null); },
      });
    }
  }
  if (search) {
    filterPills.push({
      key: 'search',
      label: 'Busca: ' + search,
      color: '#722ed1',
      icon: 'search',
      onRemove: function() { setSearch(''); },
    });
  }

  // Resets every filter (the screen reloads if the year was not the default).
  const handleClearAllFilters = persisted.resetFilters;

  var restoredSummary = filterPills
    .filter(function(pill) { return !!pill.onRemove; })
    .map(function(pill) { return pill.label; })
    .join(' · ');

  return (
    <div>
        <ModuleHeader
          breadcrumbs={['Compliance', 'IRPF']}
          title="IRPF"
          search={
            <Search
              value={search}
              placeholder="Pesquisar cliente"
              onChange={function(e) { setSearch(e.target.value); }}
            />
          }
          center={
            <>
              <Popover
                trigger="click"
                placement="bottomRight"
                content={
                  <div style={{ width: 340 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                      {!persisted.isDefault && (
                        <a
                          style={{ fontSize: 12, color: '#f5222d' }}
                          onClick={handleClearAllFilters}
                        >
                          Limpar tudo
                        </a>
                      )}
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ano</div>
                      <YearPicker
                        value={currentYear}
                        onChange={function(value) {
                          if (value) setApplied({ year: value.year() });
                        }}
                        allowClear={false}
                        allowedYear={moment().year()}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Visualização</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {[
                          { value: true, label: 'Ativos', count: activeCustomers.length, color: '#a0d911', icon: 'check-circle' },
                          { value: false, label: 'Arquivados', count: archivedCustomers.length, color: '#f5222d', icon: 'inbox' },
                        ].map(function(opt) {
                          var isActive = showActive === opt.value;
                          return (
                            <div
                              key={String(opt.value)}
                              onClick={function() { setShowActive(opt.value); }}
                              style={{
                                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                                border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'),
                                background: isActive ? opt.color + '0a' : '#fafafa',
                                transition: 'all 0.2s',
                              }}
                            >
                              <Icon type={opt.icon} style={{ fontSize: 13, color: isActive ? opt.color : '#bfbfbf' }} />
                              <span style={{ fontSize: 12, fontWeight: isActive ? 600 : 400, color: isActive ? '#262626' : '#8c8c8c' }}>
                                {opt.label}
                              </span>
                              <span style={{ fontSize: 12, fontWeight: 600, color: isActive ? opt.color : '#bfbfbf' }}>{opt.count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {STATUS_FILTER_OPTIONS.map(function(opt) {
                          var isActive = statusFilter === opt.key;
                          return (
                            <div
                              key={opt.key}
                              onClick={function() { setStatusFilter(isActive ? null : opt.key); }}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                                border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'),
                                background: isActive ? opt.color + '0a' : '#fafafa',
                                transition: 'all 0.2s',
                              }}
                            >
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: isActive ? '#262626' : '#8c8c8c' }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: opt.color, flexShrink: 0 }} />
                                {opt.label}
                              </span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: isActive ? opt.color : '#bfbfbf' }}>{opt.count}</span>
                                {isActive && <Icon type="check" style={{ color: opt.color, fontSize: 14 }} />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                }
              >
                <Badge
                  count={filterCount}
                  offset={[-4, 0]}
                  style={{ backgroundColor: '#722ed1' }}
                >
                  <Button icon="bars" style={{ borderRadius: 8 }}>Filtros</Button>
                </Badge>
              </Popover>
              <Dropdown
                trigger={['click']}
                overlay={
                  <Menu>
                    <Menu.Item key="1" onClick={showDrawer}>
                      <Icon type="plus" /> Cadastrar Cliente
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item key="2" onClick={handleExportToExcel}>
                      <Icon type="file-excel" /> Exportar para Excel
                    </Menu.Item>
                  </Menu>
                }
              >
                <Button icon="plus" shape="circle" />
              </Dropdown>
            </>
          }
          advanced={filterPills.length > 0 ? (
            <FilterPillsBar
              pills={filterPills}
              onClearAll={!persisted.isDefault ? handleClearAllFilters : undefined}
            />
          ) : null}
        />
        <RestoredFiltersNotice
          visible={persisted.wasRestored}
          summary={restoredSummary}
          onReset={handleClearAllFilters}
          onDismiss={persisted.dismissRestored}
        />

        {canSee && (
        <Row gutter={16} type="flex" style={{ alignItems: 'stretch', marginBottom: 10 }}>
          <Col span={4}>
            <Card style={{ marginBottom: 0, height: '100%' }} bodyStyle={{ padding: '10px 15px' }}>
              <Statistic
                title="Total"
                value={payment.paid.honorary + payment.paid.additionals + payment.pendent.honorary + payment.pendent.additionals}
                precision={2}
                formatter={(v) => toBRL(v)}
              />
              <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                <div>Honorário: {toBRL(payment.paid.honorary + payment.pendent.honorary)}</div>
                <div>Adicionais: {toBRL(payment.paid.additionals + payment.pendent.additionals)}</div>
              </div>
            </Card>
          </Col>
          <Col span={4}>
            <Card style={{ marginBottom: 0, height: '100%' }} bodyStyle={{ padding: '10px 15px' }}>
              <Statistic
                title="Pago"
                value={payment.paid.honorary + payment.paid.additionals}
                precision={2}
                valueStyle={{ color: '#52c41a' }}
                formatter={(v) => toBRL(v)}
              />
              <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                <div>Honorário: {toBRL(payment.paid.honorary)}</div>
                <div>Adicionais: {toBRL(payment.paid.additionals)}</div>
              </div>
            </Card>
          </Col>
          <Col span={4}>
            <Card style={{ marginBottom: 0, height: '100%' }} bodyStyle={{ padding: '10px 15px' }}>
              <Statistic
                title="Pendente"
                value={payment.pendent.honorary + payment.pendent.additionals}
                precision={2}
                valueStyle={{ color: '#f5222d' }}
                formatter={(v) => toBRL(v)}
              />
              <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                <div>Honorário: {toBRL(payment.pendent.honorary)}</div>
                <div>Adicionais: {toBRL(payment.pendent.additionals)}</div>
              </div>
            </Card>
          </Col>
          <Col span={12}>
            <Card style={{ marginBottom: 0, height: '100%' }} bodyStyle={{ padding: '10px 15px', display: 'flex', alignItems: 'center', gap: 16, height: '100%' }}>
              {(function() {
                var items = [
                  { label: 'Concluído', count: statusCounts.concluido, color: '#a0d911' },
                  { label: 'Em processo', count: statusCounts.emProcesso, color: '#fadb14' },
                  { label: 'Pendente', count: statusCounts.pendente, color: '#f5222d' },
                  { label: 'Ano anterior', count: statusCounts.anoAnterior, color: '#bfbfbf' },
                ];
                var total = items.reduce(function(s, i) { return s + i.count; }, 0);
                var size = 64;
                var r = 26;
                var cx = size / 2;
                var cy = size / 2;
                var stroke = 8;
                var circumference = 2 * Math.PI * r;
                var offset = 0;
                var arcs = [];
                if (total > 0) {
                  for (var i = 0; i < items.length; i++) {
                    if (items[i].count === 0) continue;
                    var pct = items[i].count / total;
                    var dashLen = pct * circumference;
                    arcs.push(React.createElement('circle', {
                      key: items[i].label,
                      cx: cx, cy: cy, r: r,
                      fill: 'none',
                      stroke: items[i].color,
                      strokeWidth: stroke,
                      strokeDasharray: dashLen + ' ' + (circumference - dashLen),
                      strokeDashoffset: -offset,
                      style: { transition: 'all 0.3s' }
                    }));
                    offset += dashLen;
                  }
                }
                return React.createElement(React.Fragment, null,
                  React.createElement('div', { style: { flexShrink: 0, position: 'relative', width: size, height: size } },
                    React.createElement('svg', { width: size, height: size, viewBox: '0 0 ' + size + ' ' + size, style: { transform: 'rotate(-90deg)' } },
                      React.createElement('circle', { cx: cx, cy: cy, r: r, fill: 'none', stroke: '#f0f0f0', strokeWidth: stroke }),
                      arcs
                    ),
                    React.createElement('div', { style: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' } },
                      React.createElement('span', { style: { fontSize: 15, fontWeight: 700, color: '#262626', lineHeight: 1 } }, total),
                      React.createElement('span', { style: { fontSize: 10, color: '#8c8c8c' } }, 'total')
                    )
                  ),
                  React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 3, flex: 1 } },
                    React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: '#595959', marginBottom: 2 } }, 'Status'),
                    items.map(function(item) {
                      return React.createElement('div', { key: item.label, style: { display: 'flex', alignItems: 'center', gap: 8 } },
                        React.createElement('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#595959', flexShrink: 0 } },
                          React.createElement('span', { style: { width: 8, height: 8, borderRadius: '50%', background: item.color, flexShrink: 0 } }),
                          item.label
                        ),
                        React.createElement('span', { style: { flex: 1, borderBottom: '1px dashed #e8e8e8', height: 1, margin: '0 4px' } }),
                        React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: '#262626', flexShrink: 0 } }, item.count)
                      );
                    })
                  )
                );
              })()}
            </Card>
          </Col>
        </Row>
        )}

        <TableList
          filteredData={displayedCustomers}
          isLoading={isLoading}
          onAction={onAction}
          canSee={canSee}
          currentYear={currentYear}
          showActive={showActive}
          onRowClick={showDrawer}
          loadingRowId={loadingRowId}
        />
      </div>
  );
}

const mapStateToProps = state => ({
  user: state.user,
  customers: state.irpf.customers,
  isLoading: state.irpf.isLoading,
  findUser: (id) => state.users.filter(r=>r.id===id)[0],
});

const mapDispatchProps = dispatch => ({
  showDrawer: (data = null) => dispatch(drawerIRPFActions.show(data)),
  archiveCustomer: (honorary_id, year) => dispatch(drawerIRPFActions.archive(honorary_id, year)),
  activateCustomer: (honorary_id, year) => dispatch(drawerIRPFActions.activate(honorary_id, year)),
  startService: (irpf_id, current_honorary, year) => dispatch(drawerIRPFActions.startService(irpf_id, current_honorary, year)),
});

export default connect(mapStateToProps, mapDispatchProps)(List);
